import { useRef, useState } from "react";

import { captureException } from "@freightsimple/observability/errorReporting";
import axios from "axios";

import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { isNetworkError } from "@/Helpers/isNetworkError";
import { resizeImage } from "@/Helpers/resizeImage";
import { useOnce } from "@/Hooks/useOnce";
import useQuery from "@/Hooks/useQuery";
import { useTrackingService } from "@/Services/TrackingService";
import { usePhotosApi } from "@/Hooks/usePhotosApi";
import type { ShipmentForTracking } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentForTracking";
import { ResponseError } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import { UploadPhotosShipmentDescription } from "./CustomComponents/UploadPhotosShipmentDescription";
import { UploadPhotosUploadResult } from "./CustomComponents/UploadPhotosUploadResult";
import { UploadPhotosCaptureButton } from "./CustomComponents/UploadPhotosCaptureButton";
import { UploadPhotosPhotoViewer } from "./CustomComponents/UploadPhotosPhotoViewer";
import { UploadPhotosAllSet } from "./CustomComponents/UploadPhotosAllSet";
import { UploadPhotosLoadFailed } from "./CustomComponents/UploadPhotosLoadFailed";
import { UploadPhotosPageSurface } from "./CustomComponents/Internal/UploadPhotosPageSurface";
import { UploadPhotosColumns } from "./CustomComponents/Internal/UploadPhotosColumns";
import { useUploadPhotosPreviews } from "./Hooks/useUploadPhotosPreviews";
import { useUploadPhotosLeaveWarning } from "./Hooks/useUploadPhotosLeaveWarning";

// What happened to the most recent photo. Stays on screen until the next
// capture, so the shipper can tell whether it reached the shipment.
type UploadResult = { kind: "attached" } | { kind: "failed"; message: string };

type LoadFailure = { notFound: boolean };

// A dropped connection is the customer's network, not our bug, so it is a
// console warning; any upload the API answered with an error still pages.
function reportPhotoUploadFailure(e: unknown, trackingId: string, file: File) {
  if (isNetworkError(e)) {
    console.warn("Photo upload got no response from the API", {
      trackingId,
      code: axios.isAxiosError(e) ? e.code : undefined,
    });
    return;
  }
  captureException(e, {
    component: "UploadPhotosScreen",
    action: "uploadPhotoWithTrackingId",
    trackingId,
    "file.type": file.type,
    "file.size": String(file.size),
    "axios.status": axios.isAxiosError(e)
      ? String(e.response?.status ?? "")
      : "",
  });
  console.error(`Failure uploading photo`, { e });
}

function UploadPhotosScreen() {
  const query = useQuery();
  const trackingIdParam = query["trackingId"];
  const trackingId = typeof trackingIdParam === "string" ? trackingIdParam : "";
  const photosApi = usePhotosApi();
  const trackingService = useTrackingService();
  const [shipment, setShipment] = useState<ShipmentForTracking | undefined>();
  const [loadFailure, setLoadFailure] = useState<LoadFailure | undefined>();
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    previews,
    justAttachedUrl,
    addUploading,
    markAttached,
    forgetJustAttached,
    remove,
  } = useUploadPhotosPreviews();
  const [uploadResult, setUploadResult] = useState<UploadResult | undefined>();
  const attachedCount = previews.filter((preview) => preview.attached).length;
  // Photos save as each one uploads, so Done only switches to a confirmation.
  const [done, setDone] = useState(false);
  useUploadPhotosLeaveWarning(loading);

  function pickPhoto() {
    inputRef.current?.click();
  }

  async function loadShipment() {
    setLoadFailure(undefined);
    try {
      setShipment(await trackingService.getShipmentForTracking(trackingId));
    } catch (e: unknown) {
      const status = e instanceof ResponseError ? e.response.status : undefined;
      captureException(e, {
        component: "UploadPhotosScreen",
        action: "getShipmentForTracking",
        trackingId,
        "http.status": status === undefined ? "" : String(status),
      });
      setLoadFailure({ notFound: status === 404 });
    }
  }

  useOnce(async () => {
    await loadShipment();
  });

  async function onUpload(file: File) {
    setLoading(true);
    setUploadProgress(0);
    setUploadResult(undefined);
    const previewUrl = addUploading(file);
    try {
      let upload: Blob = file;
      try {
        upload = await resizeImage(file);
      } catch (resizeError: unknown) {
        // Some browsers can't decode some photo formats (eg HEIC on Android
        // Chrome). Upload the original file instead — the backend converts it.
        captureException(resizeError, {
          component: "UploadPhotosScreen",
          action: "resizeImage",
          trackingId,
          "file.type": file.type,
          "file.size": String(file.size),
        });
      }
      await photosApi.uploadPhotoWithTrackingId(
        trackingId,
        upload,
        file.name,
        setUploadProgress,
      );
      markAttached(previewUrl);
      setUploadResult({ kind: "attached" });
    } catch (e: unknown) {
      remove(previewUrl);
      reportPhotoUploadFailure(e, trackingId, file);
      const isUnreadablePhoto =
        axios.isAxiosError(e) && e.response?.status === 422;
      setUploadResult({
        kind: "failed",
        message: isUnreadablePhoto
          ? "We couldn't read that photo file. Please try taking a new photo and uploading it again."
          : "We couldn't upload your photo. Please check your connection and try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  if (loadFailure !== undefined) {
    return (
      <UploadPhotosPageSurface>
        <UploadPhotosLoadFailed
          notFound={loadFailure.notFound}
          trackingId={trackingId}
          onRetry={() => void loadShipment()}
        />
      </UploadPhotosPageSurface>
    );
  }

  if (shipment === undefined) {
    return (
      <UploadPhotosPageSurface>
        <Stack align="center">
          <Spinner size={64} />
          <T color="secondary" align="center" testId="upload-photos-loading">
            Loading your shipment…
          </T>
        </Stack>
      </UploadPhotosPageSurface>
    );
  }

  if (done) {
    return (
      <UploadPhotosPageSurface>
        <UploadPhotosAllSet
          attachedCount={attachedCount}
          onAddMorePhotos={() => setDone(false)}
        />
      </UploadPhotosPageSurface>
    );
  }

  return (
    <UploadPhotosPageSurface>
      <Stack gap={6}>
        <Stack gap={2}>
          <Heading level={1} size="3xl" align="center">
            Photograph your freight
          </Heading>
          <T color="secondary" align="center">
            Take clear photos of every handling unit in your shipment.
          </T>
        </Stack>
        <UploadPhotosColumns
          photos={
            <>
              {!loading && uploadResult !== undefined && (
                <UploadPhotosUploadResult
                  attachedCount={attachedCount}
                  failureMessage={
                    uploadResult.kind === "failed"
                      ? uploadResult.message
                      : undefined
                  }
                />
              )}
              <UploadPhotosPhotoViewer
                photos={previews}
                uploadPercent={uploadProgress}
                justAttachedUrl={justAttachedUrl}
              />
              {!loading && (
                <>
                  <Stack align="center">
                    <UploadPhotosCaptureButton
                      afterFirstPhoto={attachedCount > 0}
                      onClick={pickPhoto}
                    />
                  </Stack>
                  {/* The visible control is the button above; this input only
                      opens the camera when that button asks it to. */}
                  <input
                    hidden
                    type="file"
                    ref={inputRef}
                    accept="image/*"
                    capture="environment"
                    name="image_uploads"
                    id="image_uploads"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file !== undefined) {
                        onUpload(file);

                        if (inputRef.current) {
                          inputRef.current.value = "";
                        }
                      }
                    }}
                  />
                </>
              )}
            </>
          }
          details={
            <>
              <UploadPhotosShipmentDescription
                pickupCompanyName={shipment.pickupLocation.businessName}
                pickupCity={shipment.pickupLocation.address.city}
                pickupState={
                  shipment.pickupLocation.address.stateOrProvinceCode
                }
                deliveryCompanyName={shipment.deliveryLocation.businessName}
                deliveryCity={shipment.deliveryLocation.address.city}
                deliveryState={
                  shipment.deliveryLocation.address.stateOrProvinceCode
                }
                lineItems={shipment.lineItems}
              />
              <T size="sm" color="secondary">
                Photos are important if the freight goes missing, is damaged or
                there is a dispute about the contents
              </T>
            </>
          }
          finish={
            attachedCount > 0 && (
              <Button
                size="large"
                onClick={() => {
                  forgetJustAttached();
                  setDone(true);
                }}
                disabled={loading}
                disabledMessage="Wait for the photo to finish uploading"
                data-testid="upload-photos-done-button"
              >
                Done
              </Button>
            )
          }
        />
      </Stack>
    </UploadPhotosPageSurface>
  );
}

export default UploadPhotosScreen;
